import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "src/components/Buttons/Button";
import {
  ButtonColors,
  ButtonSizes,
} from "src/components/Buttons/buttons.types";
import Card from "src/components/Cards/Card";
import Container from "src/components/Container/Container";
import ListCardHeader from "src/components/Lists/ListCardHeader";
import InputContainer from "src/components/Inputs/InputContainer";
import InputLabel from "src/components/Inputs/InputLabel";
import TextInput from "src/components/Inputs/TextInput";
import inputStyles from "src/components/Inputs/Inputs.module.css";
import employeeService from "../employeeService";

type BulkCreateResponse = {
  message: string;
  created_employees: {
    id: number;
    username: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    role: string;
    group: string;
    badge_num: string;
    hire_date: string;
  }[];
  failed_employees: {
    employee_data: any;
    error: string;
  }[];
  failed_count: number;
  total_created: number;
};

export default function EmployeeBulkRandomCreateForm() {
  const { t } = useTranslation("employees");
  const [successData, setSuccessData] = useState<BulkCreateResponse | null>(
    null
  );
  const [errorData, setErrorData] = useState<any>(null);
  const [confirmationText, setConfirmationText] = useState("");

  const bulkCreateMutation = useMutation({
    mutationFn: employeeService.bulkCreateRandomEmployees,
    onSuccess: (data) => {
      setSuccessData(data);
    },
    onError: (error: AxiosError) => {
      setErrorData(error?.response?.data || "An error occurred.");
    },
  });

  const handleBulkCreate = () => {
    if (confirmationText.toLowerCase() !== "clearpath") {
      setErrorData({ error: "Please type 'clearpath' to confirm" });
      return;
    }
    bulkCreateMutation.mutate();
  };

  return (
    <div className="container">
      <Card>
        <Container style={{ padding: "0 10px" }}>
          <ListCardHeader title={t("bulkRandomTitle")} />
          <Container style={{ padding: "10px", flexDirection: "column" }}>
            <div className="text-white">
              <p>
                Create 10 random employees with realistic data and the password
                "clearpath"
              </p>
              <p>This will create 10 employees with randomly generated data</p>
              <p>
                All employees will have the password:{" "}
                <strong>"clearpath"</strong>
              </p>
              <p>Employee roles will be distributed as follows:</p>
              <ul className="ml-6">
                <li>60% will be assigned to Employee roles</li>
                <li>25% will be assigned to Supervisor roles</li>
                <li>10% will be assigned to Admin roles</li>
                <li>5% will be assigned to Human Resources roles</li>
              </ul>
              <p>Random hire dates will be generated within the last 2 years</p>
              <p>All employees will be created as active users</p>
            </div>

            <div style={{ marginTop: "20px" }}>
              <div style={{ marginBottom: "20px" }}>
                <InputContainer>
                  <InputLabel htmlFor="confirmation">
                    {t("bulkRandomConfirmLabel")}
                  </InputLabel>
                  <TextInput
                    id="confirmation"
                    name="confirmation"
                    type="text"
                    value={confirmationText}
                    onChange={(e) => {
                      setConfirmationText(e.target.value);
                      if (
                        errorData?.error ===
                        "Please type 'clearpath' to confirm"
                      ) {
                        setErrorData(null);
                      }
                    }}
                    placeholder={t("bulkRandomConfirmPlaceholder")}
                    containerClassName={inputStyles.inputContainer}
                    inputClassName={inputStyles.input}
                    errorClassName={inputStyles.inputError}
                    error={
                      errorData?.error === "Please type 'clearpath' to confirm"
                        ? errorData.error
                        : null
                    }
                  />
                </InputContainer>
              </div>

              <div style={{ display: "flex", justifyContent: "center" }}>
                <Button
                  onClick={handleBulkCreate}
                  disabled={
                    bulkCreateMutation.isPending ||
                    confirmationText.toLowerCase() !== "clearpath"
                  }
                  color={ButtonColors.Green}
                  size={ButtonSizes.LG}
                >
                  {bulkCreateMutation.isPending
                    ? t("bulkRandomCreating")
                    : t("bulkRandomCreateBtn")}
                </Button>
              </div>
            </div>
          </Container>
        </Container>
      </Card>

      {bulkCreateMutation.isSuccess && successData && (
        <Card>
          <Container style={{ padding: "0 10px" }}>
            <ListCardHeader
              title={`✅ Success! ${successData.total_created} employees created`}
            />
            <Container style={{ padding: "10px", flexDirection: "column" }}>
              <div className="text-white">
                <p>{successData.message}</p>

                {successData.created_employees.length > 0 && (
                  <div style={{ marginTop: "20px" }}>
                    <h4 className="font-semibold text-white mb-2">
                      {t("bulkRandomCreatedList")}
                    </h4>
                    <div className="bg-dark-gray rounded border overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-600">
                        <thead className="bg-mid-gray">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-white uppercase">
                              {t("formFirstNameLabel")}
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-white uppercase">
                              {t("gridUsername")}
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-white uppercase">
                              {t("gridEmail")}
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-white uppercase">
                              {t("gridRole")}
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-white uppercase">
                              {t("formBadgeNumLabel")}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-dark-gray divide-y divide-gray-600">
                          {successData.created_employees.map((employee) => (
                            <tr key={employee.id}>
                              <td className="px-4 py-2 text-sm text-white">
                                {employee.first_name} {employee.last_name}
                              </td>
                              <td className="px-4 py-2 text-sm text-white font-mono">
                                {employee.username}
                              </td>
                              <td className="px-4 py-2 text-sm text-white">
                                {employee.email}
                              </td>
                              <td className="px-4 py-2 text-sm text-white">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  {employee.role} ({employee.group})
                                </span>
                              </td>
                              <td className="px-4 py-2 text-sm text-white font-mono">
                                {employee.badge_num}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {successData.failed_count > 0 && (
                  <div
                    className="bg-yellow-50 border border-yellow-200 rounded p-4"
                    style={{ marginTop: "20px" }}
                  >
                    <h4 className="font-semibold text-yellow-800 mb-2">
                      ⚠️ {successData.failed_count} employees failed to create
                    </h4>
                    <div className="text-sm text-yellow-700">
                      {successData.failed_employees.map((failed, index) => (
                        <div key={index} className="mb-2">
                          <strong>Error:</strong> {failed.error}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Container>
          </Container>
        </Card>
      )}

      {bulkCreateMutation.isError && (
        <Card>
          <Container style={{ padding: "0 10px" }}>
            <ListCardHeader title={`❌ ${t("bulkRandomErrorCreating")}`} />
            <Container style={{ padding: "10px", flexDirection: "column" }}>
              <div className="text-white">
                <h4 className="font-semibold text-white mb-2">
                  {t("bulkRandomErrorDetails")}
                </h4>
                <pre className="whitespace-pre-wrap text-sm">
                  {JSON.stringify(errorData, null, 2)}
                </pre>
              </div>
            </Container>
          </Container>
        </Card>
      )}
    </div>
  );
}
